import ContactList from "./ContactList";
import ResultsFooter from "./ResultsFooter";
import { useEffect, useRef, useState } from "react";
import ResultsHeader from "./ResultsHeader";
import { useAuth } from 'contexts/AuthContext'
// import Modal from "components/Modals/BaseModal";
import LoginModal from "components/Modals/LoginModal";
import UpgradeModal from "components/Modals/UpgradeModal";
import ContactUsModal from "components/Modals/ContactUsModal";
import PurchaseModal from "components/Modals/PurchaseModal";
import ExportModal from "components/Modals/ExportModal";
import { tryFetchWithAuth } from "utils/fetchWithAuth";
import {
    loadSearchResultsSelectionState,
    loadSearchResultsViewState,
    saveSearchResultsSelectionState,
    saveSearchResultsViewState
} from "utils/searchPersistence";

const Results = ({ results, userId }) => {

    const { user, profile } = useAuth();

    const [currentPage, setCurrentPage] = useState(1)
    const [contactsPerPage] = useState(25)
    const [selectedContacts, setSelectedContacts] = useState([]);
    const contacts = results.contacts

    const storageUserKey = userId ? String(userId) : 'anon';
    const hydratedKeyRef = useRef(new Set());

    const getTotalPages = () => {
        const total = Array.isArray(contacts) ? contacts.length : 0;
        return Math.max(1, Math.ceil(total / contactsPerPage));
    };

    const clampPage = (page) => {
        const n = Number(page);
        if (!Number.isFinite(n)) return 1;
        return Math.min(Math.max(1, Math.trunc(n)), getTotalPages());
    };

    // Restore current page (per-user) once when Results mounts for that user.
    useEffect(() => {
        if (hydratedKeyRef.current.has(storageUserKey)) return;
        hydratedKeyRef.current.add(storageUserKey);
        const view = loadSearchResultsViewState(storageUserKey);
        if (view && typeof view.currentPage !== 'undefined') {
            setCurrentPage(clampPage(view.currentPage));
        }

        const selection = loadSearchResultsSelectionState(storageUserKey);
        const ids = Array.isArray(selection?.selectedContacts) ? selection.selectedContacts : [];
        if (ids.length > 0) {
            const contactById = new Map((contacts || []).map(c => [c.contact_id, c]));
            const filtered = ids.filter((id) => {
                const c = contactById.get(id);
                if (!c) return false;
                // In normal search mode, don't restore already-saved contacts because they are disabled.
                if (results?.saved === false && c.saved === true) return false;
                return true;
            });
            if (filtered.length > 0) setSelectedContacts(filtered);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageUserKey]);

    // Clamp current page if results size changes (e.g., restored results differ).
    useEffect(() => {
        const clamped = clampPage(currentPage);
        if (clamped !== currentPage) setCurrentPage(clamped);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contacts?.length, contactsPerPage]);

    // Persist current page (per-user).
    useEffect(() => {
        saveSearchResultsViewState(storageUserKey, { currentPage });
    }, [storageUserKey, currentPage]);

    // Keep selection valid if results change (e.g., restored/new search).
    useEffect(() => {
        if (!Array.isArray(selectedContacts) || selectedContacts.length === 0) return;
        const contactById = new Map((contacts || []).map(c => [c.contact_id, c]));
        const filtered = selectedContacts.filter((id) => {
            const c = contactById.get(id);
            if (!c) return false;
            if (results?.saved === false && c.saved === true) return false;
            return true;
        });
        if (filtered.length !== selectedContacts.length) {
            setSelectedContacts(filtered);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contacts?.length, results?.saved]);

    // Persist selection (per-user).
    useEffect(() => {
        saveSearchResultsSelectionState(storageUserKey, { selectedContacts });
    }, [storageUserKey, selectedContacts]);

    // CONTACT SLICING
    const firstContactIndex = (currentPage - 1) * contactsPerPage;
    const lastContactIndex = Math.min(firstContactIndex + contactsPerPage, results.contacts.length);
    const displayedContacts = contacts.slice(firstContactIndex, lastContactIndex);

    // CONTACTS SELECTION ----------------------------------

    // modal state
    const [loginModalVisible, setLoginModalVisible] = useState(false);
    const [purchaseModalVisible, setPurchaseModalVisible] = useState(false);
    const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
    const [contactUsModalVisible, setContactUsModalVisible] = useState(false);
    const [lastAttemptedAdd, setLastAttemptedAdd] = useState(0);
    // Guard Function
    const guardSelect = (toAddCount, action) => {
        // IF NO USER, PROMPT LOGIN

        if (!results?.saved === true && !user) {
            console.log('no user');
            setLoginModalVisible(true);
            return;
        }

        // IF NO PLAN, PROMPT PURCHASE
        if (!results?.saved === true & !profile?.user_data?.plan_id) {
            console.log('no plan');
            setPurchaseModalVisible(true);
            return;
        }

        // UNLESS SELECTING ALREADY SAVED CONTACTS, ENFORCE LIMITS
        if (results?.saved===false) {
            // ENFORCE MONTHLY LIMITS
            const mLimit       = profile?.plan?.monthly_export_limit;
            const usedThisMonth= profile?.user_data?.contacts_current_period;
            const alreadyLocal = selectedContacts.length;
            const totalAfterM  = usedThisMonth + alreadyLocal + toAddCount;
            setLastAttemptedAdd(toAddCount + alreadyLocal);
        
            if (mLimit !== null && totalAfterM > mLimit) {
                setUpgradeModalVisible(true);
                return;
            }
        
            // ENFORCE DAILY LIMITS
            const dLimit       = profile?.plan?.daily_export_limit;
            const usedToday    = profile?.user_data?.contacts_today;
            const totalAfterD  = usedToday + alreadyLocal + toAddCount;
        
            if (dLimit !== null && totalAfterD > dLimit) {
                setContactUsModalVisible(true);
                return;
            }
        }
    
 
    
        // if we got here, we’re good
        action();
    };
  
      

    // Contact Selection Handlers
    const handleSelectOne = (id) => {
        const isRemoving = selectedContacts.includes(id);
        if (isRemoving) {
            // no need for guard if we're un-selecting
            return setSelectedContacts(prev => prev.filter(x => x!==id));
        }
        guardSelect(1, () => setSelectedContacts(prev => [...prev, id]));
    };
      
     // --- MODIFIED handleSelectPage ---
    // MODIFIED handleSelectPage
    const handleSelectPage = (displayed, shouldSelect) => {
        // Get IDs of all contacts displayed on the current page first
        const displayedIds = displayed.map(c => c.contact_id);

        if (!shouldSelect) {
            // Deselection logic remains unchanged
            return setSelectedContacts(prev => prev.filter(x => !displayedIds.includes(x)));
        }

        // --- START CHANGE ---
        // Determine which contacts/IDs are actually selectable based on the mode
        let selectablePageIds;
        if (results?.saved === false) {
            // In normal search mode, filter out already saved contacts
            selectablePageIds = displayed
                .filter(c => c.saved !== true)
                .map(c => c.contact_id);
        } else {
            // In saved contacts view (results.saved === true), all displayed are selectable
            selectablePageIds = displayedIds;
        }
        // --- END CHANGE ---

        // Calculate how many *new* selectable contacts are being added from this page
        const newSelectableOnes = selectablePageIds.filter(id => !selectedContacts.includes(id)).length;

        // Guard based on the count of *new selectable* contacts
        // Guard function correctly skips limits if results.saved is true
        guardSelect(newSelectableOnes, () =>
            // Update state by adding only the *selectable* page IDs
            setSelectedContacts(prev => Array.from(new Set([...prev, ...selectablePageIds])))
        );
    };


    // MODIFIED handleSelectAll
    const handleSelectAll = (shouldSelect) => {
        if (!shouldSelect) {
        // Deselection logic remains unchanged
        return setSelectedContacts([]);
        }

        // --- START CHANGE ---
        // Determine which contacts/IDs are actually selectable based on the mode
        let allSelectableIds;
        if (results?.saved === false) {
            // In normal search mode, filter out already saved contacts
            allSelectableIds = results.contacts
                .filter(c => c.saved !== true)
                .map(c => c.contact_id);
        } else {
            // In saved contacts view (results.saved === true), all results are selectable
            allSelectableIds = results.contacts.map(c => c.contact_id);
        }
        // --- END CHANGE ---

        // Calculate how many *new* selectable contacts are being added from the entire list
        const newSelectableOnes = allSelectableIds.filter(id => !selectedContacts.includes(id)).length;

        // Guard based on the count of *new selectable* contacts
        // Guard function correctly skips limits if results.saved is true
        guardSelect(newSelectableOnes, () =>
            // Update state to contain *only* the IDs of all selectable contacts
            setSelectedContacts(allSelectableIds)
        );
    };
    // --- END MODIFIED handleSelectAll ---
      

    // -----------------------------------------------------
    // EXPORT LOGIC
    const [exportLoading, setExportLoading] = useState(true);
    const [exportError, setExportError] = useState(false);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);
    
    const handleExport = async () => {
        setExportError(false);
        setExportLoading(true);
        setExportModalVisible(true);
        setExportSuccess(false);
        const { data, error, status } = await tryFetchWithAuth('/export', {
            method: 'POST',
            body: JSON.stringify({ contactIds: selectedContacts }),
        })
        setExportLoading(false); // Set loading false after fetch returns

        if (status !== 200 || error) {
            console.error("Export failed:", error, "Status:", status);
            setExportError(true);
            return; // Keep modal open showing error
        }
        if (typeof data !== 'string' || !data.trim()) {
            console.error("Export succeeded (status 200) but received invalid data:", data);
            setExportError(true); // Indicate data format issue
            return; // Keep modal open showing error
       }

        setExportSuccess(true);
        // Get the CSV file as a blob
        try {
            // Create a blob directly from the CSV string data
            const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' }); // Specify charset

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element and trigger a download
            const a = document.createElement("a");
            a.style.display = 'none'; // Hide the link
            a.href = url;
            a.download = "contacts.csv"; // The desired filename
            document.body.appendChild(a);
            a.click();

            // Clean up: remove the anchor and revoke the URL
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            // Optionally close the modal after successful download trigger
            // setTimeout(() => setExportModalVisible(false), 1000); // Example: close after 1 sec

        } catch (downloadError) {
             console.error("Error triggering CSV download:", downloadError);
             // Even though fetch succeeded, the download part failed
             // Keep success as true, but maybe show a different error?
             // For simplicity, let's just log it. The user got the success message.
             // If needed, add another state for download-specific errors.
        }
    };



    return (
        <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "400px", 
        }}>

            {loginModalVisible && (
                <LoginModal 
                    onClose={() => setLoginModalVisible(false)} 
                />
            )}
            {purchaseModalVisible && (
                <PurchaseModal onClose={()=>setPurchaseModalVisible(false)}/>
            )}
            {upgradeModalVisible && (
                <UpgradeModal
                    lastAttemptedAdd={lastAttemptedAdd}
                    contactsCurrentPeriod={profile?.user_data?.contacts_current_period}
                    selectedContacts={selectedContacts.length}
                    contactLimit={profile?.plan?.monthly_export_limit}
                    onClose={() => setUpgradeModalVisible(false)}
                    content={'Upgrade'}
                />
            )}
            {contactUsModalVisible && (
                <ContactUsModal
                    contactsToday={profile?.user_data?.contacts_today}
                    dailyContactsLimit={profile?.plan?.daily_export_limit}
                    onClose={() => setContactUsModalVisible(false)} 
                    content={'Contact Us'}
                />
            )}
            {exportModalVisible && (
                <ExportModal
                    onClose={() => setExportModalVisible(false)}
                    exportLoading={exportLoading}
                    exportError={exportError}
                    exportSuccess={exportSuccess} 
                />
            )}

            <ResultsHeader 
                results={results} 
                contacts={contacts} 
                displayedContacts={displayedContacts} 
                selectedContacts={selectedContacts} 
                setSelectedContacts={setSelectedContacts} 
                onSelectOne={handleSelectOne}
                onSelectAll={handleSelectAll}
                onSelectPage={handleSelectPage}
                onClearSelected={() => setSelectedContacts([])}
                setIsExportModalVisible={setExportModalVisible}
                handleExport={handleExport}
            />

            <div style={{flexGrow: 1, overflow: "auto"}}>
                <ContactList 
                    results={results}
                    contacts={displayedContacts} 
                    selectedContacts={selectedContacts} 
                    onSelectOne={handleSelectOne}
                />
            </div>

            <ResultsFooter 
                currentPage={currentPage} 
                setCurrentPage={setCurrentPage} 
                firstContactIndex={firstContactIndex} 
                lastContactIndex={lastContactIndex} 
                totalContactsLength={contacts.length} 
                contactsPerPage={contactsPerPage}
            />
        </div>
    );
    };

export default Results;
